import type { ZoneInput } from '@chukua/common';
import { Alert, Button, Form, Input, Select } from 'antd';
import clsx from 'clsx';
import type { Zone, ZoneKind } from './reference-data.api';
import type { ZoneMapValue } from './use-zone-map-editor';
import { ZONE_EDITOR_GRID, ZONE_MAP_HEIGHT } from './zone-editor-layout';
import { ZoneMapEditor } from './ZoneMapEditor';

interface ZoneFormProps {
  /** The zone being edited, or null for a new one. */
  zone: Zone | null;
  /** Active zone kinds — the deployment's own vocabulary (gitaumoses4/chukua#21), not a hardcoded list. */
  zoneKinds: ZoneKind[];
  mapTileKey: string | null;
  error: string | null;
  submitting: boolean;
  onSave: (input: ZoneInput) => Promise<void>;
  onCancel: () => void;
}

/** The zone's own kind might since have been deactivated (excluded from `zoneKinds`) — offer it anyway, so the Select has something to display. */
function kindOptions(zoneKinds: ZoneKind[], zoneKind: string | undefined) {
  const known = zoneKinds.some((k) => k.code === zoneKind);
  const options = zoneKind && !known ? [{ code: zoneKind, name: zoneKind }, ...zoneKinds] : zoneKinds;
  return options.map((k) => ({ value: k.code, label: k.name }));
}

function initialValues(zone: Zone | null, zoneKinds: ZoneKind[]): ZoneInput {
  if (!zone) return { code: '', name: '', kind: zoneKinds[0]?.code ?? '', country: '', polygon: null, centroid: null };
  return { code: zone.code, name: zone.name, kind: zone.kind, country: zone.country, polygon: zone.polygon, centroid: zone.centroid };
}

/**
 * The zone's fields beside a map that edits its polygon and centroid. Those two are ordinary (hidden)
 * form fields, not separate React state, so the map's onChange is a plain form update.
 */
export function ZoneForm({ zone, zoneKinds, mapTileKey, error, submitting, onSave, onCancel }: ZoneFormProps) {
  const [form] = Form.useForm<ZoneInput>();
  const watchedPolygon = Form.useWatch('polygon', form);
  const watchedCentroid = Form.useWatch('centroid', form);
  // useWatch is undefined until the form mounts; after that null means "cleared", so only fall back before then.
  const polygon = watchedPolygon === undefined ? (zone?.polygon ?? null) : watchedPolygon;
  const centroid = watchedCentroid === undefined ? (zone?.centroid ?? null) : watchedCentroid;

  function setMapValue(value: ZoneMapValue) {
    form.setFieldsValue({ polygon: value.polygon, centroid: value.centroid });
  }

  return (
    <Form<ZoneInput>
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={onSave}
      disabled={submitting}
      initialValues={initialValues(zone, zoneKinds)}
      className={ZONE_EDITOR_GRID}
    >
      <section className="flex flex-col rounded-lg border border-line bg-surface p-5">
        {error && <Alert className="mb-5" type="error" title={error} showIcon />}
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="kind" label="Kind" rules={[{ required: true, message: 'Choose a kind' }]}>
          <Select options={kindOptions(zoneKinds, zone?.kind)} />
        </Form.Item>
        <Form.Item name="country" label="Country" rules={[{ required: true, message: 'Enter a country' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="polygon" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="centroid" hidden>
          <Input type="hidden" />
        </Form.Item>
        <div className="mt-auto flex gap-2 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {zone ? 'Save' : 'Create zone'}
          </Button>
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        </div>
      </section>
      <section aria-label="Polygon and centroid" className={clsx('min-w-0', ZONE_MAP_HEIGHT)}>
        <ZoneMapEditor tileKey={mapTileKey} value={{ polygon, centroid }} onChange={setMapValue} />
      </section>
    </Form>
  );
}
